import React from 'react';
import { Mic, MicOff, Video, VideoOff, Settings, X, ChevronDown, Loader2, Info } from 'lucide-react';
import { MediaDevice } from '../../types/interviews';

interface DeviceControlsProps {
  isCameraEnabled: boolean;
  isMicEnabled: boolean;
  toggleCamera: () => void;
  toggleMicrophone: () => void;
  isDeviceDropdownOpen: boolean;
  toggleDeviceDropdown: () => void;
  videoDevices: MediaDevice[];
  audioDevices: MediaDevice[];
  selectedVideoDevice: string;
  selectedAudioDevice: string;
  handleDeviceChange: (deviceId: string, type: 'video' | 'audio') => void;
  isTogglingCamera?: boolean;
  isTogglingMic?: boolean;
  disableMicToggle?: boolean;
}

const DeviceControls: React.FC<DeviceControlsProps> = ({
  isCameraEnabled,
  isMicEnabled,
  toggleCamera,
  toggleMicrophone,
  isDeviceDropdownOpen,
  toggleDeviceDropdown,
  videoDevices,
  audioDevices,
  selectedVideoDevice,
  selectedAudioDevice,
  handleDeviceChange,
  isTogglingCamera = false,
  isTogglingMic = false,
  disableMicToggle = false
}) => {
  return (
    <div className="flex flex-col items-center space-y-2">
      <div className="flex items-center justify-center space-x-4 z-20">
        {/* Device settings dropdown toggle */}
        <div className="relative">
          <button
            onClick={toggleDeviceDropdown}
            className="rounded-full p-3 bg-surface-light dark:bg-surface-dark text-text-primary-light dark:text-text-primary-dark border border-border-light dark:border-border-dark transition-all duration-300 hover:scale-110"
            aria-label="Device settings"
          >
            <Settings className="w-4 h-4 md:w-5 md:h-5" />
          </button>
          
          {/* Device selection dropdown */}
          {isDeviceDropdownOpen && (
            <div className="absolute bottom-full left-0 mb-2 w-72 max-w-[90vw] bg-surface-light dark:bg-surface-dark rounded-lg border border-border-light dark:border-border-dark shadow-lg overflow-hidden z-50">
              <div className="p-3">
                <div className="flex justify-between items-center mb-2">
                  <h4 className="text-base font-semibold text-text-primary-light dark:text-text-primary-dark">Device Settings</h4>
                  <button 
                    onClick={toggleDeviceDropdown}
                    className="p-1 rounded-full hover:bg-background-light/20 dark:hover:bg-background-dark/20"
                  >
                    <X className="w-4 h-4 text-text-secondary-light dark:text-text-secondary-dark" />
                  </button>
                </div>
                <h4 className="text-base font-medium text-text-primary-light dark:text-text-primary-dark mb-2">Camera</h4>
                <div className="relative mb-3">
                  <select 
                    value={selectedVideoDevice}
                    onChange={(e) => handleDeviceChange(e.target.value, 'video')}
                    className="w-full p-2 appearance-none bg-background-light/70 dark:bg-background-dark/70 rounded-lg border border-border-light dark:border-border-dark text-sm pr-8"
                  >
                    {videoDevices.map(device => (
                      <option key={device.deviceId} value={device.deviceId}>
                        {device.label}
                      </option>
                    ))}
                    {videoDevices.length === 0 && (
                      <option value="">No cameras found</option>
                    )}
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                    <ChevronDown className="w-4 h-4 text-text-secondary-light dark:text-text-secondary-dark" />
                  </div>
                </div>
                
                <h4 className="text-base font-medium text-text-primary-light dark:text-text-primary-dark mb-2">Microphone</h4>
                <div className="relative">
                  <select 
                    value={selectedAudioDevice}
                    onChange={(e) => handleDeviceChange(e.target.value, 'audio')}
                    className="w-full p-2 appearance-none bg-background-light/70 dark:bg-background-dark/70 rounded-lg border border-border-light dark:border-border-dark text-sm pr-8"
                  >
                    {audioDevices.map(device => (
                      <option key={device.deviceId} value={device.deviceId}>
                        {device.label}
                      </option>
                    ))}
                    {audioDevices.length === 0 && (
                      <option value="">No microphones found</option>
                    )}
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                    <ChevronDown className="w-4 h-4 text-text-secondary-light dark:text-text-secondary-dark" />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Camera toggle button */}
        <button
          onClick={toggleCamera}
          disabled={isTogglingCamera}
          className={`rounded-full p-3 transition-all duration-300 ${!isTogglingCamera ? 'hover:scale-110' : ''} ${isCameraEnabled 
            ? 'bg-primary-light dark:bg-primary-dark text-white shadow-md shadow-primary-light/20 dark:shadow-primary-dark/20' 
            : 'bg-surface-light dark:bg-surface-dark text-text-primary-light dark:text-text-primary-dark border border-border-light dark:border-border-dark'}`}
        >
          {isTogglingCamera ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            isCameraEnabled ? <Video className="w-4 h-4 md:w-5 md:h-5" /> : <VideoOff className="w-4 h-4 md:w-5 md:h-5" />
          )}
        </button>
        
        {/* Microphone toggle button */}
        <button
          onClick={toggleMicrophone}
          disabled={isTogglingMic || disableMicToggle}
          className={`rounded-full p-3 transition-all duration-300 ${!isTogglingMic ? 'hover:scale-110' : ''} ${isMicEnabled 
            ? 'bg-primary-light dark:bg-primary-dark text-white shadow-md shadow-primary-light/20 dark:shadow-primary-dark/20' 
            : 'bg-surface-light dark:bg-surface-dark text-text-primary-light dark:text-text-primary-dark border border-border-light dark:border-border-dark'}`}
        >
          {isTogglingMic ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            isMicEnabled ? <Mic className="w-4 h-4 md:w-5 md:h-5" /> : <MicOff className="w-4 h-4 md:w-5 md:h-5" />
          )}
        </button>
      </div>
    </div>
  );
};

export default DeviceControls; 